/**
 * Terms of Service Page
 */

import { FileText, CreditCard, AlertTriangle, Scale } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import './docs.scss';

export function TermsOfService() {
  const { t } = useI18n();

  const definitionItems = t('tos.definitions.items').split('|');
  const freeUsageItems = t('tos.service.freeUsage.items').split('|');
  const tokenUsageItems = t('tos.service.tokenUsage.items').split('|');
  const accountItems = t('tos.account.items').split('|');
  const tokenPurchaseItems = t('tos.tokens.purchase.items').split('|');
  const tokenRefundItems = t('tos.tokens.refund.items').split('|');
  const thirdPartyItems = t('tos.thirdParty.items').split('|');
  const permittedItems = t('tos.use.permitted.items').split('|');
  const prohibitedItems = t('tos.use.prohibited.items').split('|');
  const disclaimerItems = t('tos.disclaimer.items').split('|');
  const liabilityItems = t('tos.liability.items').split('|');
  const terminationByUserItems = t('tos.termination.byUser.items').split('|');
  const terminationByUsItems = t('tos.termination.byUs.items').split('|');

  return (
    <div className="docs-content tos-page">
      <h1>{t('tos.title')}</h1>
      <p><em>{t('tos.lastUpdated')}</em></p>

      {/* Introduction */}
      <section className="tos-page__section">
        <h2>
          <FileText size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('tos.intro.title')}
        </h2>
        <p>{t('tos.intro.content')}</p>
        <p>{t('tos.intro.agreement')}</p>
      </section>

      {/* Definitions */}
      <section className="tos-page__section">
        <h2>{t('tos.definitions.title')}</h2>
        <ul>
          {definitionItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </section>

      {/* Service Description */}
      <section className="tos-page__section">
        <h2>{t('tos.service.title')}</h2>
        <p>{t('tos.service.overview')}</p>

        <div className="tos-page__highlight">
          <h4>{t('tos.service.freeUsage.title')}</h4>
          <ul>
            {freeUsageItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="tos-page__token-info">
          <h4>
            <CreditCard size={18} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
            {t('tos.service.tokenUsage.title')}
          </h4>
          <ul>
            {tokenUsageItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Account Registration */}
      <section className="tos-page__section">
        <h2>{t('tos.account.title')}</h2>
        <div className="tos-page__important">
          <p>{t('tos.account.age')}</p>
        </div>
        <p>{t('tos.account.content')}</p>
        <ul>
          {accountItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <p>{t('tos.account.security')}</p>
        <div className="tos-page__warning">
          <p>{t('tos.account.inactivity')}</p>
        </div>
      </section>

      {/* Notifications and Communications */}
      <section className="tos-page__section">
        <h2>{t('tos.notifications.title')}</h2>
        <p>{t('tos.notifications.methods')}</p>
        <p>{t('tos.notifications.consent')}</p>
      </section>

      {/* Tokens and Payment */}
      <section className="tos-page__section">
        <h2>
          <CreditCard size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('tos.tokens.title')}
        </h2>

        <h3>{t('tos.tokens.purchase.title')}</h3>
        <ul>
          {tokenPurchaseItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <div className="tos-page__highlight">
          <p>{t('tos.tokens.tax')}</p>
        </div>
        <div className="tos-page__warning">
          <p>{t('tos.tokens.expiration')}</p>
        </div>
        <div className="tos-page__warning">
          <p>{t('tos.tokens.promotional')}</p>
        </div>

        <h3>{t('tos.tokens.consumption.title')}</h3>
        <div className="tos-page__highlight">
          <p>{t('tos.tokens.consumption.content')}</p>
        </div>

        <h3>{t('tos.tokens.refund.title')}</h3>
        <div className="tos-page__warning">
          <ul>
            {tokenRefundItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Free Services (Third-Party API Keys) */}
      <section className="tos-page__section">
        <h2>{t('tos.thirdParty.title')}</h2>
        <p>{t('tos.thirdParty.content')}</p>
        <ul>
          {thirdPartyItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <p>{t('tos.thirdParty.noWarranty')}</p>
      </section>

      {/* Acceptable Use */}
      <section className="tos-page__section">
        <h2>{t('tos.use.title')}</h2>

        <h3>{t('tos.use.permitted.title')}</h3>
        <ul>
          {permittedItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>

        <h3>{t('tos.use.prohibited.title')}</h3>
        <div className="tos-page__important">
          <ul>
            {prohibitedItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Intellectual Property */}
      <section className="tos-page__section">
        <h2>{t('tos.ip.title')}</h2>
        <p>{t('tos.ip.ownership')}</p>
        <p>{t('tos.ip.license')}</p>
        <p>{t('tos.ip.userContent')}</p>
      </section>

      {/* Disclaimers */}
      <section className="tos-page__section">
        <h2>
          <AlertTriangle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('tos.disclaimer.title')}
        </h2>
        <div className="tos-page__warning">
          <h4>{t('tos.disclaimer.asIs')}</h4>
          <ul>
            {disclaimerItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
        <p>{t('tos.disclaimer.accuracy')}</p>
        <p>{t('tos.disclaimer.availability')}</p>
      </section>

      {/* Limitation of Liability */}
      <section className="tos-page__section">
        <h2>{t('tos.liability.title')}</h2>
        <div className="tos-page__important">
          <ul>
            {liabilityItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Indemnification */}
      <section className="tos-page__section">
        <h2>{t('tos.indemnification.title')}</h2>
        <p>{t('tos.indemnification.content')}</p>
      </section>

      {/* Termination */}
      <section className="tos-page__section">
        <h2>{t('tos.termination.title')}</h2>

        <h3>{t('tos.termination.byUser.title')}</h3>
        <ul>
          {terminationByUserItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>

        <h3>{t('tos.termination.byUs.title')}</h3>
        <ul>
          {terminationByUsItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <div className="tos-page__highlight">
          <p>{t('tos.termination.discontinuation')}</p>
        </div>
      </section>

      {/* Changes to Terms */}
      <section className="tos-page__section">
        <h2>{t('tos.changes.title')}</h2>
        <p>{t('tos.changes.content')}</p>
      </section>

      {/* Governing Law */}
      <section className="tos-page__section">
        <h2>
          <Scale size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('tos.governing.title')}
        </h2>
        <div className="tos-page__highlight">
          <p>{t('tos.governing.informalResolution')}</p>
        </div>
        <p>{t('tos.governing.law')}</p>
        <p>{t('tos.governing.jurisdiction')}</p>
        <p>{t('tos.governing.language')}</p>
      </section>

      {/* General Provisions */}
      <section className="tos-page__section">
        <h2>{t('tos.general.title')}</h2>
        <p>{t('tos.general.entireAgreement')}</p>
        <p>{t('tos.general.severability')}</p>
        <p>{t('tos.general.waiver')}</p>
        <div className="tos-page__highlight">
          <p><strong>Force Majeure:</strong> {t('tos.general.forceMajeure')}</p>
        </div>
      </section>

      {/* Contact */}
      <section className="tos-page__section">
        <h2>{t('tos.contact.title')}</h2>
        <p>{t('tos.contact.content')}</p>
        <div className="tos-page__contact">
          <p>{t('tos.contact.legal')}</p>
          <p>{t('tos.contact.support')}</p>
          <p>{t('tos.contact.github')}</p>
        </div>
      </section>
    </div>
  );
}
